import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { useData } from '../providers/DataProvider';
import { prefersReducedMotion } from '../lib/visualEffects';

export default function ThemeToggle({ className = '' }) {
  const data = useData();
  const preferences = data?.preferences || {};
  const savePreferences = data?.savePreferences;

  const prefersDarkList =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  const systemPrefersDark = prefersDarkList ? prefersDarkList.matches : false;

  const prefTheme = preferences.theme ?? 'dark';
  const resolvedTheme = prefTheme === 'system' ? (systemPrefersDark ? 'dark' : 'light') : prefTheme;

  const toggle = () => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    if (typeof savePreferences === 'function') {
      const maybePromise = savePreferences({ theme: next });
      if (maybePromise?.catch) {
        maybePromise.catch((err) => console.warn('No se pudo guardar el tema', err));
      }
    } else {
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', next === 'dark');
      }
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem('theme', next);
        } catch (e) {
          console.warn('No se pudo persistir el tema', e);
        }
      }
    }
  };

  const isDark = resolvedTheme === 'dark';

  const reduceMotion = React.useMemo(() => prefersReducedMotion(), []);

  return (
    <motion.button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 transition-colors ${className}`}
      title={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
      whileHover={reduceMotion ? undefined : { scale: 1.05 }}
      whileTap={reduceMotion ? undefined : { scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 260, damping: 16 }}
      layout
    >
      {isDark ? (
        <Moon className="w-4 h-4 text-blue-500" />
      ) : (
        <Sun className="w-4 h-4 text-yellow-400" />
      )}
      <span className="text-sm">{isDark ? 'Oscuro' : 'Claro'}</span>
    </motion.button>
  );
}
